/**
 * Public SMS opt-in page (WO 2026-07-27, A2P/10DLC campaign requirement).
 *
 * The GHL chat widget is declared to TCR as the ONLY opt-in consent form on the
 * website URL — do not add any other lead/contact/appointment form to this page.
 *
 * Public route, no login. Widget id is per GHL sub-account (per site):
 * parameterized in SITE_WIDGETS, never hardcoded in the JSX.
 */
import React, { useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Link, Alert } from '@mui/material';

const GHL_LOADER_SRC = 'https://widgets.leadconnectorhq.com/loader.js';
const GHL_RESOURCES_URL = 'https://widgets.leadconnectorhq.com/chat-widget/loader.js';

// One GHL sub-account (and therefore one widget id) per site.
// Add ids here as each site's sub-account gets its widget.
const SITE_WIDGETS = {
  rockland: {
    widgetId: '6a67a9692bb8c2db8ee89642',
    displayName: 'HASC Rockland (Gan Ezra)',
    officePhone: '(845) 356-0191',
  },
  // '14th-ave': { widgetId: '...', displayName: 'HASC 14th Avenue', officePhone: '...' },
  // '55th-street': { widgetId: '...', displayName: 'HASC 55th Street', officePhone: '...' },
  // 'woodmere': { widgetId: '...', displayName: 'HASC Woodmere', officePhone: '...' },
};

const DEFAULT_SITE = 'rockland';

function useGhlChatWidget(widgetId) {
  useEffect(() => {
    if (!widgetId) return undefined;
    const script = document.createElement('script');
    script.src = GHL_LOADER_SRC;
    script.setAttribute('data-resources-url', GHL_RESOURCES_URL);
    script.setAttribute('data-widget-id', widgetId);
    script.setAttribute('data-source', 'WEB_USER');
    document.body.appendChild(script);
    return () => {
      // Best-effort cleanup so the chat bubble does not follow users into the app.
      script.remove();
      document.querySelectorAll('chat-widget, [id^="lc_text-widget"]').forEach((el) => el.remove());
    };
  }, [widgetId]);
}

export default function SmsOptInPage() {
  const { site } = useParams();
  const siteKey = (site || DEFAULT_SITE).toLowerCase();
  const cfg = SITE_WIDGETS[siteKey];

  useGhlChatWidget(cfg?.widgetId);

  if (!cfg) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2, sm: 3 }, py: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
          Text Message Updates
        </Typography>
        <Alert severity="info">
          Online text-message opt-in is not yet available for this location. Please contact your
          school office to sign up for text updates.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2, sm: 3 }, py: 4 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 600 }}>
        Text Message Updates — {cfg.displayName}
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
        Sign up to receive school-related text messages from {cfg.displayName}.
      </Typography>

      <Box sx={{ fontSize: '1rem', lineHeight: 1.7, '& p': { mb: 2 } }}>
        <Typography component="p">
          <strong>How to opt in:</strong> use the chat bubble in the corner of this page to send us
          your name, your child&apos;s name, and the mobile number you would like us to text. By
          submitting your mobile number through the chat, you agree to receive school-related text
          messages from {cfg.displayName} (program updates, schedule changes, closures, emergencies,
          transportation, attendance, health notices, and reminders).
        </Typography>
        <Typography component="p">
          Message frequency varies. Message and data rates may apply. Reply <strong>STOP</strong> at
          any time to cancel, or <strong>HELP</strong> for help. Consent is optional and is{' '}
          <strong>not a condition of enrollment</strong> or of receiving any program or service. You
          can revoke consent at any time by replying STOP or by calling the school office at{' '}
          {cfg.officePhone}.
        </Typography>
        <Typography component="p">
          See our <Link component={RouterLink} to="/privacy">Privacy Policy</Link> and{' '}
          <Link component={RouterLink} to="/messaging-terms">Messaging Terms</Link>. We do not sell,
          rent, or share your phone number or opt-in information with third parties for their
          marketing purposes.
        </Typography>
        <Typography component="p" sx={{ color: 'text.secondary' }}>
          Questions? Call the office at {cfg.officePhone}.
        </Typography>
      </Box>
    </Box>
  );
}
